/**
 * Where every window and every gutter of the workspace sits, in pixels. Pure, so the
 * arithmetic is tested without a browser; `components/workspace.tsx` draws from it.
 *
 * A window's stored share (`SplitNode.sizes`) is a fraction of its split, and a
 * minimised one keeps its share untouched, so restoring gives back the exact size it
 * had. What changes is only the drawing: along its split's axis a minimised window
 * takes `barPx` (its title bar), and its open siblings share the rest in proportion
 * to their own stored sizes. A split whose children are all minimised is itself
 * minimised along its parent's axis.
 */
import type { LayoutNode, SplitDir } from "@/lib/workspace";

/** A minimised window's extent along its split: the 32px title bar plus the frame's 3px padding and 1px border each side. */
export const MINIMIZED_PX = 40;

export interface PxBox {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface Frame extends PxBox {
  /** Drawn as its title bar only: `horizontal` across a stack, `vertical` as a strip beside a window. */
  bar: "horizontal" | "vertical" | null;
}
/**
 * A draggable boundary between `children[index]` and `children[index + 1]` of the
 * split at `path`, both open. `pos` is where it is drawn (px along the split's axis,
 * from the workspace's origin). A pointer at `p` px means a stored boundary of
 * `startFrac + (p - origin) / unit`, and `unit` (px per stored fraction) is the
 * extent `resize` measures minimums against.
 */
export interface Gutter {
  key: string;
  path: number[];
  index: number;
  dir: SplitDir;
  split: PxBox;
  pos: number;
  origin: number;
  unit: number;
  startFrac: number;
  /** The stored boundary now: `startFrac + sizes[index]`. */
  at: number;
}
export interface Arrangement {
  frames: Map<string, Frame>;
  gutters: Gutter[];
}

/** A minimised window, or a split whose every child is. */
export function isCollapsed(node: LayoutNode): boolean {
  return node.type === "window" ? Boolean(node.minimized) : node.children.every(isCollapsed);
}

/** What a collapsed node takes along `dir`: a bar each when they stack that way, else one bar. */
function collapsedExtent(node: LayoutNode, dir: SplitDir, barPx: number): number {
  if (node.type === "window" || node.dir !== dir) return barPx;
  return node.children.reduce((sum, child) => sum + collapsedExtent(child, dir, barPx), 0);
}

export function arrange(root: LayoutNode, opts: { width: number; height: number; barPx?: number }): Arrangement {
  const barPx = opts.barPx ?? MINIMIZED_PX;
  const frames = new Map<string, Frame>();
  const gutters: Gutter[] = [];
  const walk = (node: LayoutNode, box: PxBox, path: number[]) => {
    if (node.type === "window") {
      frames.set(node.id, { ...box, bar: node.minimized ? (box.w < box.h ? "vertical" : "horizontal") : null });
      return;
    }
    const row = node.dir === "row";
    const total = row ? box.w : box.h;
    const start = row ? box.x : box.y;
    const collapsed = node.children.map(isCollapsed);
    // Every child minimised: this split was given its space by its parent anyway, so they share it as stored.
    const allCollapsed = collapsed.every(Boolean);
    const sizes = node.children.map((_, index) => node.sizes[index] ?? 1 / node.children.length);
    const openSum = allCollapsed ? 1 : sizes.reduce((sum, size, index) => (collapsed[index] ? sum : sum + size), 0);
    const barTotal = allCollapsed ? 0 : node.children.reduce((sum, child, index) => (collapsed[index] ? sum + collapsedExtent(child, node.dir, barPx) : sum), 0);
    const unit = Math.max(0, total - barTotal) / (openSum || 1);
    const extents = node.children.map((child, index) => (!allCollapsed && collapsed[index] ? collapsedExtent(child, node.dir, barPx) : sizes[index]! * unit));
    let offset = start;
    let frac = 0;
    node.children.forEach((child, index) => {
      const extent = extents[index]!;
      walk(child, row ? { x: offset, y: box.y, w: extent, h: box.h } : { x: box.x, y: offset, w: box.w, h: extent }, [...path, index]);
      const origin = offset;
      offset += extent;
      const startFrac = frac;
      frac += sizes[index]!;
      if (index < node.children.length - 1 && !allCollapsed && !collapsed[index] && !collapsed[index + 1]) {
        gutters.push({ key: `${path.join(".")}:${index}`, path, index, dir: node.dir, split: box, pos: offset, origin, unit, startFrac, at: frac });
      }
    });
  };
  walk(root, { x: 0, y: 0, w: Math.max(0, opts.width), h: Math.max(0, opts.height) }, []);
  return { frames, gutters };
}
